import React, { createContext, useContext, useState } from 'react';

type EventHandler<T> = (payload: T) => void;
type EventHandlers<T> = { [eventName: string]: EventHandler<T>[] };

interface EventBusContextType<T> {
	addEventListener: (eventName: string, handler: EventHandler<T>) => void;
	removeEventListener: (eventName: string, handler: EventHandler<T>) => void;
	dispatchEvent: (eventName: string, payload: T) => void;
}

const EventBusContext = createContext<EventBusContextType<any> | null>(null);

export function EventBusProvider<T>({ children }: { children: React.ReactNode }) {
	const [eventHandlers, setEventHandlers] = useState<EventHandlers<T>>({});

	const addEventListener = (eventName: string, handler: EventHandler<T>) => {
		setEventHandlers((prevHandlers) => ({
			...prevHandlers,
			[eventName]: (prevHandlers[eventName] || []).concat(handler),
		}));
	};

	const removeEventListener = (eventName: string, handler: EventHandler<T>) => {
		setEventHandlers((prevHandlers) => ({
			...prevHandlers,
			[eventName]: (prevHandlers[eventName] || []).filter((h) => h !== handler),
		}));
	};

	const dispatchEvent = (eventName: string, payload: T) => {
		const handlers = eventHandlers[eventName] || [];
		handlers?.forEach((handler) => handler(payload));
	};

	return (
		<EventBusContext.Provider value={{ addEventListener, removeEventListener, dispatchEvent }}>
			{children}
		</EventBusContext.Provider>
	);
}

export function useEventBus<T>(): EventBusContextType<T> {
	const eventBusContext = useContext(EventBusContext);
	if (!eventBusContext) {
		throw new Error('useEventBus must be used within an EventBusProvider');
	}
	return eventBusContext as EventBusContextType<T>;
}
